import React, {useState, useMemo, useEffect} from "react";
import {Bubble, Checkbox, Form, Radio, StatusTip, Select, Dropdown, List, SearchBox, Switch} from "tea-component";
import SvgIcon from "@home/components/SvgIcon";
import { IScopeChart, ScopeType } from '@dbl/services/dashboard';
import { getTableInfoList } from '@dbl/services/dataSource';
import { ChartType } from '@dbl/core/chart';
import { FieldTypeIcon } from '@dbl/core/dataSource';
import {useRequest, useUpdateEffect} from "ahooks";
import TextBubbles from "@home/components/TextBubbles";
import {getLocalText} from "@/pages/datalab/utils/utils";
import classNames from "classnames";
import { useIntl } from "umi";
import { LANGUAGE_EN } from "@home/_config/config";

const {LoadingTip, ErrorTip} = StatusTip;

export interface FilterScopeChart {
  chartType: string;
  id: string;
  name: string;
  name_cn: string;
  datasetList?: any[];
  columnDatasetId: string; // 字段所在的数据表id
  isDefaultDate?: boolean; // 趋势图和数字卡片设置日期筛选器时,是否展示最新时间的数据
}

export default function FilterScope(props: any) {
  const { locale, formatMessage: intlFormatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const formatMessage = (id: string) => {
    // 旧组件内部使用简短 key，这里统一映射为 useIntl 的完整 key
    const keyMap: Record<string, string> = {
      scope_setting: "dbl_create_dashboard_scope_setting",
      scope_same: "dbl_create_dashboard_scope_same",
      scope_same_info: "dbl_create_dashboard_scope_same_info",
      scope_specific: "dbl_create_dashboard_scope_specific",
      scope_specific_info: "dbl_create_dashboard_scope_specific_info",
      scope_join_placeholder: "dbl_create_dashboard_scope_join_placeholder",
      scope_join_error: "dbl_create_dashboard_scope_join_error",
      scope_join_empty: "dbl_create_dashboard_scope_join_empty",
      disabled_default_date: "dbl_create_dashboard_disabled_default_date",
      default_date_info: "dbl_create_dashboard_default_date_info",
      default_date: "dbl_create_dashboard_default_date",
      search: "dbl_common_search",
    };
    const fullKey = keyMap[id] || id;
    return intlFormatMessage({ id: fullKey });
  };

  const {dataset, value, onChange, validStatus, clearValidStatus, scopeChartsMap={}, filterType, gameCode, defaultDateDisabled} = props;
  const {scopeType, scopeCharts=[]} = value || {};
  const [isHover, setIsHover] = useState<string>('');
  
  useUpdateEffect(() => {
    // 当用户设置日期筛选器, 且打开对比时间时, 那么关闭Default date开关, 且禁用开关.
    if (defaultDateDisabled) {
      const newCharts = scopeCharts.map(item => {
        item.isDefaultDate = false;
        return item;
      });
      onChange({scopeType, scopeCharts: newCharts});
    }
  }, [defaultDateDisabled]);

  return (<>			
    <Form.Item label={formatMessage('scope_setting')} className={`dbl-exit-filter-scope${isEn ? ' en' : ''}`}>
      <Radio.Group value={scopeType} onChange={(value) => {
        const sameDatasetCharts = scopeChartsMap?.sameDatasetCharts?.map((chart: FilterScopeChart) => ({task_id: String(chart.id), columnDatasetId: dataset, name: chart.name, nameCn: chart.name_cn}));
        onChange({scopeType: value, scopeCharts: sameDatasetCharts || []});
        clearValidStatus();
      }}>
        <Radio name={ScopeType.SameDataset}>
          {formatMessage('scope_same')}
          <Bubble
            dark
            content={formatMessage('scope_same_info')}
            placementOffset={[18, 10]}
            placement={'top-end'}
            visible={isHover === ScopeType.SameDataset}
            // visible={true}
            overlayClassName={'dbl-exit-filter-scope-info-bubble'}
          >
				        <span className="dbl-exit-filter-scope-info" onMouseOver={() => {
					        setIsHover(ScopeType.SameDataset);
				        }} onMouseOut={() => {
					        setIsHover('');
				        }}>
				          <SvgIcon type={isHover === ScopeType.SameDataset ? 'alert-circle-action' : 'alert-circle-m'}/>
				        </span>
          </Bubble>
        </Radio>
        <Radio name={ScopeType.SpecificChart} disabled={scopeChartsMap?.specificCharts?.length === 0}>
          {formatMessage('scope_specific')}
          <Bubble
            dark
            content={formatMessage('scope_specific_info')}
            placementOffset={[20, 10]}
            placement={'top-end'}
            visible={isHover === ScopeType.SpecificChart}
            overlayClassName={'dbl-exit-filter-scope-info-bubble'}
          >
            <span className="dbl-exit-filter-scope-info" onMouseOver={() => {
              setIsHover(ScopeType.SpecificChart);
            }} onMouseOut={() => {
              setIsHover('');
            }}>
              <SvgIcon type={isHover === ScopeType.SpecificChart ? 'alert-circle-action' : 'alert-circle-m'}/>
				       </span>
          </Bubble>
        </Radio>
      </Radio.Group>
    </Form.Item>
    {
      dataset ? (<div className={'dbl-exit-filter-scope-charts'}>
        {
          scopeChartsMap?.sameDatasetCharts?.map((chart: FilterScopeChart) => {
            const temp = scopeCharts.find((item: IScopeChart) => String(chart.id) === String(item.task_id));
            return (
              <DefaultDateSwitch
                key={chart.id}
                formatMessage={formatMessage}
                isShowDefaultDate={filterType === 'date' && (chart.chartType === ChartType.Trend || chart.chartType === ChartType.DimNumberTrend || chart.chartType === ChartType.BigNumber)}
                disabled={!temp || defaultDateDisabled}
                onChange={(value: boolean) => {
                  if (temp) {
                    temp.isDefaultDate = value;
                    onChange({scopeType, scopeCharts: [...scopeCharts]});
                  }
                }}
                value={(!defaultDateDisabled) && temp?.isDefaultDate}
              >
                <Checkbox
                  key={chart.id}
                  className={'dbl-exit-filter-scope-charts-check-name'}
                  value={!!temp}
                  onChange={(checked) => {
                    if (checked) {
          
                      onChange({scopeType, scopeCharts: [...scopeCharts, {task_id: String(chart.id), columnDatasetId: dataset, name: chart.name, nameCn: chart.name_cn}]});
                    } else {
                      onChange({scopeType, scopeCharts: scopeCharts.filter((item: IScopeChart) => chart.id !== item.task_id)});
                    }
                  }}
                >
                  {getLocalText(isEn, chart?.name_cn, chart?.name)}
                </Checkbox>
  
              </DefaultDateSwitch>
            );
          })
        }
        <div className="tea-form__item" style={{display: scopeType === ScopeType.SameDataset ? 'none' : 'block'}}>
          {
            scopeChartsMap?.specificCharts?.map((chart: FilterScopeChart) => {
              const temp = scopeCharts.find((item: IScopeChart) => chart?.id === item.task_id && chart.columnDatasetId === item.columnDatasetId);
              // fix:sql数据源修改成简易模式后，原来被引用的sql字段筛选器失效，但是筛选器编辑页可以点击confirm
              // 【【DataLab】【体验优化】报表筛选器：sql更新后，交互优化】https://tapd.tencent.com/tapd_fe/20427644/story/detail/1020427644119543367
              if(temp && !temp?.apply_to_column) temp.apply_to_column = [];
              return (
                <DefaultDateSwitch
                  key={chart?.id}
                  formatMessage={formatMessage}
                  isShowDefaultDate={filterType === 'date' && (chart.chartType === ChartType.Trend || chart.chartType === ChartType.DimNumberTrend || chart.chartType === ChartType.BigNumber)}
                  disabled={!temp || defaultDateDisabled}
                  onChange={(value: boolean) => {
                    if (temp) {
                      temp.isDefaultDate = value;
                      onChange({scopeType, scopeCharts: [...scopeCharts]});
                    }
                  }}
                  value={(!defaultDateDisabled) && temp?.isDefaultDate}
                >
                  <ChartNameAndJoinField
                    gameCode={gameCode}
                    key={chart?.id}
                    checkDefaultValue={!!temp}
                    onCheckChange={(checked: boolean) => {
                      if (checked) {
                        onChange({scopeType, scopeCharts: [...scopeCharts, {task_id: String(chart?.id), columnDatasetId: dataset, name: chart.name, nameCn: chart.name_cn, apply_to_column: []}]});
                      } else {
                        onChange({scopeType, scopeCharts: scopeCharts.filter((item: IScopeChart) => chart?.id !== item?.task_id)});
                      }
                    }}
                    onJoinFieldChange={(field: {}) => {
        
                      const oldChartIndex = scopeCharts.findIndex((item: IScopeChart) => chart?.id === item?.task_id);
                      if (oldChartIndex >= 0) {
                        const newChart = {
                          ...scopeCharts[oldChartIndex],
                          isCheck: undefined,
                          apply_to_column: [field]
                        };
                        const newCharts = [...scopeCharts]; // 保证 字段的变化, 可以将置灰的保存按钮变为可点击
                        newCharts[oldChartIndex] = newChart;
                        onChange({scopeType, scopeCharts: newCharts});
                      } else {
                        onChange({
                          scopeType,
                          scopeCharts: [...scopeCharts, {task_id: String(chart?.id), columnDatasetId: dataset, apply_to_column: [field], noCheck: true}]
                        });
                      }
                      validStatus[chart?.id] && clearValidStatus(chart?.id);
        
                    }}
                    joinField={scopeCharts.find((item: IScopeChart) => chart?.id === item?.task_id)?.apply_to_column?.[0]}
                    chartInfo={chart}
                    isEn={isEn}
                    formatMessage={formatMessage}
                    validStatus={validStatus[chart?.id]}
                    scopeType={scopeType}
                  />
                </DefaultDateSwitch>
              );
            })
          }
        </div>
      </div>) : ''
    }
  </>
	
  );
}

function ChartNameAndJoinField(props: any) {
	
  const {
    chartInfo,
    isEn,
    formatMessage,
    onCheckChange,
    checkDefaultValue,
    onJoinFieldChange,
    joinField,
    validStatus,
    scopeType,
    gameCode,
  } = props;
	
  const {name, name_cn, datasetList} = chartInfo;

  const {data, run, loading, error} = useRequest(getTableInfoList, {
    manual: true,
  });

  const mapOfTable = useMemo(() => {
    const map: any = {};
    try {
      data?.forEach((table: any) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {table_id, table_name, customize_name, customize_name_en, column_list} = table || {};
        const newOptions = column_list.map((column: any) => {
          const content = getLocalText(isEn, column?.display_name_en, column?.display_name, column?.name);
          return {
            ...column,
            value: `${column.name},${column.type}`,
            text: (<div title={content} className={'dbl-exit-filter-scope-charts-join-field'}><SvgIcon
              className={FieldTypeIcon[column.type] || "abc"} type={FieldTypeIcon[column.type] || "abc"}/>{content}</div>),
            content,
          };
        }) || [];
        map[table_id] = {
          table_id,
          table_name: getLocalText(isEn, customize_name, customize_name_en, table_name),
          table_code: datasetList.find((dataset: { table_id: any; }) => dataset.table_id === table_id).table_code,
          options: newOptions,
        };
      });
    } catch (e) {
      console.log('FilterScope mapOfOptions error', e);
    }
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEn]);

  const onOpen = () => {
    if (!data && !loading) {
      run(
        datasetList.map((dataset: { table_id: any }) => dataset.table_id),
        gameCode,
      );
    }
  };
  
  useEffect(() => {
    // 如果是之前已选择了关联字段, 则初始渲染时加载选项
    checkDefaultValue && onOpen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

	
  // eslint-disable-next-line no-nested-ternary
  const tips = loading ? <LoadingTip/> : (
    error ? <ErrorTip onRetry={() => run(datasetList.map((dataset: { table_id: any; }) => dataset.table_id))}/> : null
  );

  return (<><div className={'dbl-exit-filter-scope-charts-join'}>
    <Checkbox
      value={checkDefaultValue}
      onChange={onCheckChange}
    >
      <TextBubbles text={getLocalText(isEn, name_cn, name)} depend={scopeType} />
    </Checkbox>
    <SvgIcon className={''} style={{color: "#9b9fb0"}} type={'join'}/>
    
    {
      datasetList?.length > 1 ? (
        <MoreTablesJoinField
          onOpen={onOpen}
          mapOfTable={mapOfTable}
          onJoinFieldChange={onJoinFieldChange}
          tips={tips}
          joinField={joinField}
          placeholder={formatMessage('scope_join_placeholder')}
          validStatus={validStatus}
          isEn={isEn}
          formatMessage={formatMessage}
        />
      ) :
				
        (
          <Select
            className={`dbl-exit-filter-scope-charts-join-select${validStatus ? ' error' : ''}`}
            overlayClassName={''}
            overlayStyle={{width: '207px'}}
            appearance="button"
            options={mapOfTable?.[datasetList?.[0]?.table_id]?.options || []}
            searchable
            filter={(inputValue, option) => optionMatchesKeyword(isEn, option, inputValue)}
            value={`${joinField?.column},${joinField?.column_type}`}
            onChange={(value) => {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              const {table_id, table_code} = mapOfTable?.[datasetList[0]?.table_id] || {};
              // eslint-disable-next-line @typescript-eslint/naming-convention
              const [column, column_type] = value.split(',');
              onJoinFieldChange({
                table_id,
                table_code,
                column,
                column_type,
              });
            }}
            onOpen={onOpen}
            placeholder={formatMessage('scope_join_placeholder')}
            tips={tips}
          />
          
        )
    }
  </div>
  
  {
    validStatus ? (<div className={'dbl-exit-filter-scope-charts-join_error'}>
      {formatMessage(`scope_join_${validStatus}`)}
    </div>) : ''
  }
  </>);
}

function MoreTablesJoinField(props: any) {
  const {onOpen, mapOfTable, onJoinFieldChange, tips, joinField, placeholder, validStatus, isEn, formatMessage} = props;
  const [currentTable, setCurrentTable] = useState<{table_id: string; table_code: string; column: string; column_type: string;}>({table_id: '', table_code: '', column: '', column_type: ''});
  const [button, setButton] = useState(placeholder);
  const [searchResult, setSearchResult] = useState(null);
  useEffect(() => {
		
    if (joinField) {
      const opt = mapOfTable[currentTable?.table_id]?.options?.find((opt: { value: string; }) => (opt.value === `${joinField.column},${joinField.column_type}`));
      setButton(opt?.text);
      setCurrentTable(joinField);
    } else {
      setButton(placeholder);
      setCurrentTable(Object.values(mapOfTable)?.[0]);
    }
		
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinField, mapOfTable]);
	
  const tableMap = searchResult || mapOfTable;
	
  return (
    <Dropdown
      // className={`dbl-exit-filter-scope-charts-join-dropdown${validStatus ? ' error' : ''}`}
      className={classNames('dbl-exit-filter-scope-charts-join-dropdown', {
        'error': validStatus,
        'placeholder': !joinField
      })}
      appearance="button"
      button={button}
      onOpen={onOpen}
      clickClose={false}
    >
				
      {
        (close) => (<div className={'dbl-exit-filter-scope-charts-join-dropdown-content'}>
						
          {
            tips ? tips : (<>
								
              <SearchBox
                onChange={(searchStr) => {
                  const trimmed = searchStr?.trim() ?? '';
                  const keywordUpper = trimmed ? trimmed.toUpperCase() : '';

                  if (keywordUpper) {
                    const result: any = {};
                    let firstMatchedTable: any = null;

                    Object.values(mapOfTable)?.forEach((table: any) => {
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      const {table_id, options=[]} = table;
                      const searchOption = options.filter((opt: any) => optionMatchesKeyword(isEn, opt, keywordUpper));

                      result[table_id] = {
                        ...table,
                        options: searchOption,
                      };

                      if (!firstMatchedTable && searchOption?.length) {
                        firstMatchedTable = result[table_id];
                      }
                    });

                    setSearchResult(result);
                    if (firstMatchedTable) {
                      setCurrentTable(firstMatchedTable as any);
                    }
                  } else {
                    setSearchResult(null);
                    setCurrentTable(Object.values(mapOfTable)?.[0] as any);
                  }
                }}
                onClear={() => {
                  setSearchResult(null);
                  setCurrentTable(Object.values(mapOfTable)?.[0] as any);
                }}
                placeholder={formatMessage('search')}
              />
								
              <List
                className={'content-left-table-name'}
                type={'option'}
              >
                <span className={'title'}>{formatMessage('data_source')}</span>
                {
                  Object.values(tableMap)?.map((table: {table_name: string; table_id: string;}) => {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const {table_name, table_id} = table;
                    return (
                      <List.Item
                        key={table_id}
                        onClick={() => {
                          setCurrentTable(table);
                        }}
                        selected={currentTable?.table_id === table_id}
                      >
                        <span title={table_name}>{table_name}</span>
													
                      </List.Item>);
                  })
                }
              </List>
								
              <List
                type={'option'}
                className={'content-right-fields'}
              >
                <span className={'title'}>{formatMessage('column')}</span>
                {
                  tableMap[currentTable?.table_id]?.options?.map((opt: { text: any; value: any; }) => {
                    const {text, value} = opt;
                    return (
                      <List.Item
                        key={value}
                        onClick={() => {
                          // eslint-disable-next-line @typescript-eslint/naming-convention
                          const [column, column_type] = value.split(',');
                          onJoinFieldChange({
                            table_id: currentTable?.table_id,
                            table_code: currentTable?.table_code,
                            column,
                            column_type,
                          });
                          close();
                        }}
                        selected={`${currentTable.column},${currentTable.column_type}` === value}
                      >
                        {text}
                      </List.Item>);
                  })
                }
              </List>
							
            </>)
							
          }
					
					
        </div>)
      }
			
			
    </Dropdown>);
}

/**
 * 判断选项是否匹配搜索关键字（不区分大小写）
 * 同时校验显示名称（根据语言环境取 display_name_en 或 display_name）和字段名 name
 * @param isEn - 是否为英文环境
 * @param option - 待检查的选项对象
 * @param keyword - 用户输入的搜索关键字
 * @returns 当显示名称或字段名包含关键字时返回 true，否则返回 false
 */
function optionMatchesKeyword(isEn: boolean, option: any, keyword: string): boolean {
  if (!option) {
    return false;
  }
  if (!(keyword ?? '').trim()) {
    return true;
  }
  const keywordUpper = keyword.toUpperCase();
  const displayName = isEn ? option?.display_name_en : option?.display_name;
  const keywordInDisplayName = (displayName ?? '').toUpperCase().includes(keywordUpper);
  const keywordInName = (option?.name ?? '').toUpperCase().includes(keywordUpper);
  return keywordInDisplayName || keywordInName;
}

function DefaultDateSwitch(props: any) {
  const {isShowDefaultDate, formatMessage, children, ...otherProps} = props;
  const [iconName, setIconName] = useState('alert-circle-m');
  
  return (<div className={'dbl-exit-filter-scope-charts-item'}>
    {children}
  
    {
      isShowDefaultDate ? (<div className={'dbl-scope-default-date'}>
        <Bubble
          content={otherProps?.disabled ? formatMessage('disabled_default_date') : null}
        >
          <Switch {...otherProps}/>
          {formatMessage('default_date')}
        </Bubble>
        <Bubble
          dark
          content={formatMessage('default_date_info')}
          arrowPointAtCenter
          placement={'top-end'}
          className={'dbl_scope_default_date_info_bubble'}
        >
          <span className="dbl-exit-filter-scope-info" onMouseOver={() => {
            setIconName('alert-circle-action');
          }} onMouseOut={() => {
            setIconName('alert-circle-m');
          }}>
            <SvgIcon type={iconName}/>
          </span>
        </Bubble>
        
      </div>) : ''
    }
    
  </div>);
  
}
